/**
 * Bakes the nebula into cubemaps at load (spec §8, contract §4.5): the background cube at the preset's face size
 * and a small cube that PMREM filters into `scene.environment`. Both targets are 8-bit sRGB (the sky is never
 * HDR: the star is a separate impostor and the key light), which halves VRAM against half float
 * (1024² faces: 24 MiB instead of 48 MiB) with no visible banding thanks to the bake's dither.
 */
import {
  BackSide,
  BoxGeometry,
  CubeCamera,
  CubeRenderTarget,
  LinearFilter,
  Mesh,
  NodeMaterial,
  NoBlending,
  PMREMGenerator,
  RenderTarget,
  Scene,
  SRGBColorSpace,
  UnsignedByteType,
  type Node,
  type WebGPURenderer,
} from 'three/webgpu'

export interface SkyBaker {
  /** Renders the nebula into a new cube render target of `size`² per face. */
  bakeCube(size: number): CubeRenderTarget
  /** Re-renders the nebula into an existing cube target. */
  bakeInto(target: CubeRenderTarget): void
  /** Filters a baked cube into the PMREM (reusing the previous PMREM target). */
  pmrem(cube: CubeRenderTarget): RenderTarget
  dispose(): void
}

/** Time until the GPU has finished all submitted work (WebGPU), else just a microtask. */
export async function gpuIdle(renderer: WebGPURenderer): Promise<void> {
  const device = (renderer.backend as { device?: { queue: { onSubmittedWorkDone(): Promise<void> } } }).device
  if (device) await device.queue.onSubmittedWorkDone()
}

/** Bytes of a baked RGBA8 cube without mips. */
export function cubeBytes(size: number): number {
  return size * size * 4 * 6
}

export function createSkyBaker(renderer: WebGPURenderer, node: Node<'vec4'>): SkyBaker {
  const material = new NodeMaterial()
  material.name = 'sky:bake'
  material.fragmentNode = node
  material.side = BackSide
  material.blending = NoBlending
  material.depthTest = false
  material.depthWrite = false
  const box = new BoxGeometry(2, 2, 2)
  const mesh = new Mesh(box, material)
  mesh.frustumCulled = false
  const scene = new Scene()
  scene.add(mesh)
  const generator = new PMREMGenerator(renderer)
  let pmremTarget: RenderTarget | null = null
  const render = (target: CubeRenderTarget): void => {
    const camera = new CubeCamera(0.1, 10, target)
    const mrt = renderer.getMRT()
    renderer.setMRT(null)
    camera.update(renderer, scene)
    renderer.setMRT(mrt)
  }

  return {
    bakeCube(size: number): CubeRenderTarget {
      const target = new CubeRenderTarget(size, { type: UnsignedByteType, colorSpace: SRGBColorSpace, generateMipmaps: false, depthBuffer: false })
      target.texture.minFilter = LinearFilter
      target.texture.magFilter = LinearFilter
      target.texture.name = `sky:bake:${size}`
      render(target)
      return target
    },
    bakeInto(target: CubeRenderTarget): void {
      render(target)
    },
    pmrem(cube: CubeRenderTarget): RenderTarget {
      pmremTarget = generator.fromCubemap(cube.texture, pmremTarget)
      pmremTarget.texture.name = 'sky:pmrem'
      return pmremTarget
    },
    dispose(): void {
      box.dispose()
      material.dispose()
      generator.dispose()
      pmremTarget?.dispose()
    },
  }
}
